import { CalendarDays, ChevronsUpDown, ListChecks, LogOut, UserRound } from "lucide-react";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo } from "react";

import AppSidebarHeader from "@/components/Layouts/Navbar/SignedIn/sidebar-header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { removeBlanksFromObj } from "@winshop-crm/lib";
import { UserDataType } from "@winshop-crm/types";
import { WSAvatar } from "@winshop-crm/ui";

import { useAppSelector } from "../../../../store";

const AppSidebar = ({ user }: { user: UserDataType | null }) => {
  const router = useRouter();

  const eventsFilter = useAppSelector((state) => state.filters.eventsFilter);
  const tasksFilter = useAppSelector((state) => state.filters.tasksFilter);

  const pathName = useMemo(() => `/${router.pathname.split("/")[1]}`, [router.pathname]);
  const navigation = useMemo(
    () => [
      {
        name: "Události",
        icon: CalendarDays,
        href: { pathname: "/events", query: removeBlanksFromObj(eventsFilter) },
      },
      {
        name: "Úkoly",
        icon: ListChecks,
        href: { pathname: "/tasks", query: removeBlanksFromObj(tasksFilter) },
      },
    ],
    [eventsFilter, tasksFilter]
  );

  if (!user) {
    return null;
  }

  return (
    <Sidebar collapsible="icon">
      <AppSidebarHeader />

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navigation.map((item) => (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton
                  render={
                    <Link href={item.href}>
                      <item.icon />
                      <span>{item.name}</span>
                    </Link>
                  }
                  isActive={pathName === item.href.pathname}
                  tooltip={item.name}
                />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton
                    size="lg"
                    tooltip={user.fullName}
                    className="data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground"
                  >
                    <WSAvatar userName={user.fullName} src={user.photo} size="sm" />
                    <div className="grid flex-1 text-sm leading-tight text-left">
                      <span className="font-medium truncate">{user.fullName}</span>
                      <span className="text-xs truncate text-neutral-textSubtle">{user.email}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                }
              />
              <DropdownMenuContent side="right" align="end" className="w-56">
                <DropdownMenuItem render={<Link href="/user/settings" />}>
                  <UserRound />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut({ callbackUrl: "/" })}>
                  <LogOut />
                  Odhlásit se
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
